import axios from "axios";
import axiosInstance from "../../utils/axiosConfig";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../constant";
import { BiErrorCircle } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";
import { apiQueryKey } from "../../lib/api/queryKeys";

type ConfirmResolveModalProps = {
  setResolveConfirmModal: (open: boolean) => void;
  setResolveSuccessModal: (open: boolean) => void;
  selectedPayouts: string[];
  onResolved?: () => void;
};

const ConfirmResolveModal = ({
  setResolveConfirmModal,
  setResolveSuccessModal,
  selectedPayouts,
  onResolved,
}: ConfirmResolveModalProps) => {
  const queryClient = useQueryClient();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(numericValue);
  };

  const resolveMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.post(`${API_URL}api/payouts/resolve-failed-payouts`, {
        otp,
        payoutIds: selectedPayouts,
      });
    },
    onSuccess: () => {
      onResolved?.();
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-payouts-failed"),
      });
      setResolveConfirmModal(false);
      setResolveSuccessModal(true);
    },
    onError: (e: unknown) => {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : "Confirmation failed.";
      setError(String(msg));
      window.setTimeout(() => setError(""), 4000);
    },
  });

  const confirmTransaction = () => {
    if (resolveMutation.isPending || otp.length !== 6) return;
    setError("");
    resolveMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-[145] flex items-end justify-center overflow-y-auto bg-black/70 p-0 backdrop-blur-[2px] sm:items-center sm:p-4 font-manrope"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default"
        onClick={() => setResolveConfirmModal(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-resolve-title"
        className="relative z-[146] w-full max-w-md rounded-t-2xl border border-[#2C363E] bg-[#13181B] p-5 shadow-2xl sm:rounded-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id="confirm-resolve-title"
              className="font-sora text-lg font-bold text-white"
            >
              Confirm resolution
            </h2>
            <p className="mt-1 text-[13px] leading-relaxed text-[#869AA9]">
              Enter the 6-digit admin OTP sent to your device. This confirms
              resolving {selectedPayouts.length} payout
              {selectedPayouts.length === 1 ? "" : "s"}.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setResolveConfirmModal(false)}
            className="shrink-0 rounded-lg border border-[#3A4852] px-2.5 py-1.5 text-[11px] font-semibold text-[#AEBCC6] hover:bg-[#273037]"
          >
            Cancel
          </button>
        </div>

        <div className="mt-6">
          <label
            htmlFor="resolve-admin-otp"
            className="text-[12px] font-semibold text-[#869AA9]"
          >
            One-time code
          </label>
          <input
            id="resolve-admin-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            placeholder="••••••"
            onChange={handleChange}
            className="mt-2 w-full rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5 font-mono text-lg tracking-[0.35em] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-2 focus:ring-secondary_button/25"
          />
          <p className="mt-2 text-[11px] text-[#5C6B76]">
            {otp.length}/6 digits
          </p>
        </div>

        {error ? (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-3 text-[13px] font-medium text-red-200">
            <BiErrorCircle className="shrink-0 text-xl" aria-hidden />
            {error}
          </div>
        ) : null}

        <button
          type="button"
          onClick={confirmTransaction}
          disabled={otp.length !== 6 || resolveMutation.isPending}
          className={
            otp.length === 6 && !resolveMutation.isPending
              ? "mt-6 flex min-h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[14px] font-bold text-white transition hover:brightness-110"
              : "mt-6 flex min-h-[48px] w-full cursor-not-allowed items-center justify-center rounded-xl bg-[#2C363E] text-[14px] font-bold text-[#5C6B76]"
          }
        >
          {resolveMutation.isPending ? (
            <TailSpin color="#FFFFFF" height={28} width={28} />
          ) : (
            "Confirm & resolve"
          )}
        </button>
      </div>
    </div>
  );
};

export default ConfirmResolveModal;
